import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getWidgetDeepLinkTarget,
  consumePendingWidgetDeepLink,
  cleanWidgetParamsFromUrl,
  markDeepLinkConsumed,
} from '@/lib/deep-link-utils';

const SESSION_STORAGE_KEY = 'deeplink_projectId';

/**
 * Handles incoming deep links by reading widget params (`page`/`entities`)
 * or `projectId` from URL search params (with sessionStorage fallback for
 * OIDC redirect scenarios).
 *
 * Priority:
 * 1. Widget deep-link from current URL params (`?page=Projects&entities=123`)
 * 2. Pending widget deep-link from sessionStorage (survives OIDC redirect)
 * 3. `projectId` URL param
 * 4. `projectId` from sessionStorage
 *
 * Stale workspace restores are detected earlier (at module scope in
 * `persistWidgetDeepLink` / `listenForEarlyNavigationRequest`) before
 * React even renders, so this component only handles fresh deep links.
 *
 * Must be rendered inside the Router.
 */
export function DeepLinkHandler(): null {
  const navigate = useNavigate();

  useEffect(() => {
    const widgetTarget = getWidgetDeepLinkTarget();
    if (widgetTarget) {
      consumePendingWidgetDeepLink();
      cleanWidgetParamsFromUrl();
      markDeepLinkConsumed(widgetTarget);
      navigate(widgetTarget, { replace: true });
      return;
    }

    const pendingTarget = consumePendingWidgetDeepLink();
    if (pendingTarget) {
      markDeepLinkConsumed(pendingTarget);
      navigate(pendingTarget, { replace: true });
      return;
    }

    // Only handle projectId deep-link on initial page load — reading directly
    // from the current URL so we don't re-run on every internal navigation that
    // legitimately uses ?projectId= as a filter param (e.g. Budget Variance,
    // Project Forecast reports).
    const initialParams = new URLSearchParams(window.location.search);
    let projectId = initialParams.get('projectId');

    if (!projectId) {
      try {
        projectId = sessionStorage.getItem(SESSION_STORAGE_KEY);
      } catch {
        // sessionStorage unavailable
      }
    }

    if (projectId) {
      try {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      } catch {
        // sessionStorage unavailable
      }

      const target = `/projects/${projectId}`;
      markDeepLinkConsumed(target);
      navigate(target, { replace: true });
      return;
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only — deep-links are an initial load concern

  return null;
}
